<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  // When null the Save button is hidden (read-only / deferred sections).
  saving: { type: Boolean, default: null },
  // Com rascunho (`useRascunhoDaSecao`): sem edição, o botão vira o estado "Salvo", apagado;
  // com edição, o Salvar sólido. null = seção sem rascunho, o botão de sempre.
  suja: { type: Boolean, default: null },
});

defineEmits(['save']);

const { t } = useI18n();

// A faixa de LEITURA (papel `agent`, 22/09/2026) chega aqui e não em cada seção. São onze
// seções, cada uma com seus controles; pedir que cada autora lembre de desabilitá-los é
// pedir que uma esqueça — e a que esquecer deixa o leitor editando um campo que o servidor
// vai recusar no save.
//
// `<fieldset disabled>` desabilita TODO controle de formulário aninhado pelo próprio HTML,
// inclusive os que ainda não existem. É por isso que é um fieldset e não uma varredura de
// `:disabled` componente a componente: a garantia vale para a seção que alguém escrever
// depois sem saber que esta faixa existe.
//
// O botão Save fica FORA do fieldset de propósito — some por `v-if`, e não por estar
// desabilitado. Botão cinza convida a clicar e a descobrir; botão ausente já respondeu.
const somenteLeitura = inject(
  'powerAgentSomenteLeitura',
  computed(() => false)
);
</script>

<template>
  <!-- mx-auto: sem ele o max-w-3xl limita a largura mas nao centraliza, e a coluna
       cola na borda esquerda de um <section> que nao centraliza nada. -->
  <div class="flex flex-col w-full max-w-3xl gap-6 mx-auto">
    <header class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">{{ title }}</h2>
        <p v-if="description" class="mt-1 text-sm text-n-slate-11">
          {{ description }}
        </p>
      </div>
      <Button
        v-if="saving !== null && !somenteLeitura"
        :label="
          suja === false
            ? t('POWER_AGENTS.SAVED_STATE')
            : t('POWER_AGENTS.SAVE')
        "
        :icon="suja === false ? 'i-lucide-check' : undefined"
        :color="suja === false ? 'slate' : 'blue'"
        :variant="suja === false ? 'ghost' : 'solid'"
        :disabled="suja === false"
        size="sm"
        :is-loading="saving"
        data-salvar-secao
        class="shrink-0"
        @click="$emit('save')"
      />
    </header>

    <!-- FLOW-13 (SPEC S1; PLAN G19): as sub-abas da seção ficam FORA do fieldset. A faixa de leitura
         desliga todo botão de dentro dele, e o gestor do cofre (o `poweragents_manage`, que lê o
         resto do agente) precisa trocar para a sub-aba "Segredos", onde ele mexe. -->
    <slot name="abas" />

    <p
      v-if="somenteLeitura"
      class="px-3 py-2 text-xs rounded-lg text-n-slate-11 bg-n-alpha-1"
    >
      {{ t('POWER_AGENTS.SOMENTE_LEITURA') }}
    </p>

    <!-- min-w-0: o fieldset nasce com `min-inline-size: min-content`, e nenhum reset o tira.
         Sem isto ele não fica mais estreito que a linha mais larga de dentro (todo `truncate`
         conta inteiro), e no celular a seção inteira passava da tela e rolava de lado. -->
    <fieldset
      :disabled="somenteLeitura"
      class="flex flex-col min-w-0 gap-5 p-0 m-0 border-0"
    >
      <slot />
    </fieldset>
  </div>
</template>
